import { IconInfo } from "@powerledger/ui-component-lib";
import { TFunction } from "i18next";
import { Column, Row } from "react-table";
import { Flex, Text } from "theme-ui";

import { EligibilityValueFormatter } from "@/app/components/eligibility-value-formatter";
import { LocationValueWrapper } from "@/app/components/location-value-wrapper/location-value-wrapper";
import { TableValueWrapper } from "@/app/components/table-value-wrapper";
import { AllRecOptions } from "@/app/hooks/use-all-rec-options";
import { CertificationAndEliglibilityType } from "@/app/lib/format-attributes";
import { RECOrderAttributeOptions } from "@/app/lib/format-rec-options";
import { getFuelSourceIcon } from "@/app/lib/get-fuel-source-icon";
import { isErcotRecAttributes } from "@/app/lib/registry-attributes-helpers";
import { OrderPosition } from "@/app/types/generated/graphql";
import { TraceXSupportedRegistries } from "@/app/types/okta.types";

import { RecHoldingsTableData } from "../rec-holdings/rec-holdings.types";

export function getCommonRecTableData(
  t: TFunction,
  recOptions?: RECOrderAttributeOptions, // Remove this parameter once we have Any Registry everywhere this function is used
  followErcotNamingConvention?: boolean,
  options?: {
    includeRegistryName?: boolean;
    recOptionsByRegistryCode?: AllRecOptions["recOptionsByRegistryCode"];
  },
) {
  const tableColumns: Column<RecHoldingsTableData>[] = [
    {
      Header: t("Vintage"),
      accessor: (data: RecHoldingsTableData) => data.vintage,
      Cell: ({ value }: { value: string }) => {
        return <TableValueWrapper value={value} />;
      },
      disableSortBy: true,
    },
    {
      Header: t(followErcotNamingConvention ? "FIN" : "Project"),
      accessor: "project",
      Cell: ({ value }: { value: string }) => {
        return <TableValueWrapper value={t(value) || ""} />;
      },
      disableSortBy: true,
    },
    {
      Header: t("Location"),
      accessor: "locations",
      Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => {
        const { location, registryCode } = row.original;
        return (
          <LocationValueWrapper
            value={location ? [location] : ["-"]}
            recOptions={options?.recOptionsByRegistryCode?.[registryCode as TraceXSupportedRegistries]}
          />
        );
      },
      disableSortBy: true,
    },
    {
      Header: t(followErcotNamingConvention ? "Certification" : "Eligibility"),
      accessor: "certificationsAndEligibilities",
      Cell: ({ value }: { value: CertificationAndEliglibilityType[] }) => {
        return (
          <EligibilityValueFormatter
            certificationsAndEligibilities={value}
            position={OrderPosition.Ask}
            title={followErcotNamingConvention ? "Certifications" : "Eligibilities"}
          />
        );
      },
      disableSortBy: true,
    },

    {
      Header: t(followErcotNamingConvention ? "Technology Type" : "Fuel Type"),
      Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => {
        const { fuelSources, registryCode } = row.original;
        return (
          <Flex sx={{ color: "textDarker" }}>
            {!fuelSources || fuelSources.length < 1 ? (
              "-"
            ) : (
              <TableValueWrapper
                title={followErcotNamingConvention ? "Technology Type" : "Fuel Type"}
                getIcon={getFuelSourceIcon}
                value={fuelSources}
                getToolTipText={(key: string) =>
                  options?.recOptionsByRegistryCode?.[
                    registryCode as TraceXSupportedRegistries
                  ]?.fuelSourceOptions?.find((x) => x.value === key)?.label || ""
                }
              />
            )}
          </Flex>
        );
      },
      disableSortBy: true,
    },
    {
      Header: t("Commencement Date (COD)"),
      id: "commencementDate",
      Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => (
        <TableValueWrapper value={row.original.attributes?.commencementDate ?? "-"} />
      ),
      disableSortBy: true,
    },
    {
      Header: t("Certificate Type"),
      accessor: "certificateType",
      disableSortBy: true,
      Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => {
        const certificateType = isErcotRecAttributes(row.original.attributes)
          ? row.original?.certificateType ?? "-"
          : "-";
        return <TableValueWrapper value={certificateType} />;
      },
    },
    ...(options?.includeRegistryName
      ? [
          {
            Header: t("Registry"),
            Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => (
              <TableValueWrapper containerSx={{ minWidth: 60 }} value={row.original.registryDisplayName ?? ""} />
            ),
            disableSortBy: true,
          },
        ]
      : []),
    {
      Header: () => (
        <Flex sx={{ alignItems: "center" }}>
          <Text>{t("Balance")}</Text>
          <IconInfo
            size={5}
            color="success.800"
            sx={{ ml: 1 }}
            data-tip={t(
              "The available balance for your account may differ from the current balance because of pending transactions that have been presented against the account, but have not yet been processed. ",
            )}
          />
        </Flex>
      ),
      id: "balance",
      Cell: ({ row }: { row: Row<RecHoldingsTableData> }) => (
        <Text sx={{ color: "textDarker" }}>
          {`${new Intl.NumberFormat().format(row.original.availableBalance)} / ${new Intl.NumberFormat().format(
            row.original.currentBalance,
          )}`}
        </Text>
      ),
    },
  ];
  return tableColumns;
}
